import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";
import { ArrowRight, CheckCircle2, Handshake, Hotel, Waves, Building2, Loader2 } from "lucide-react";
import { submitPartnerApplication } from "@/lib/partner.functions";
import { faqJsonLd, serviceJsonLd } from "@/lib/seo.schemas";
import { toast } from "sonner";

const FAQ = [
  { q: "Vad innebär ett partnerskap?", a: "Återkommande kunder — hotell, simhallar, äventyrsbad och konferensanläggningar — får ramavtals­pris, prioriterad bokning, dedikerad kontakt­person och möjlighet att lagerhålla material hos oss för snabba leveranser." },
  { q: "Är det bindande?", a: "Nej. Vårt partneravtal är en ramöverenskommelse — ni bokar varje event som vanligt. Avtalet ger er priser och prioritet, inte volymkrav." },
  { q: "Vilka brukar bli partner?", a: "Hotell med återkommande konferensgäster, simhallar och äventyrsbad som vill ha vattenaktiviteter under sommarsäsong, konferensanläggningar med teambuilding-paket, samt kommuner med flera lovveckor per år." },
  { q: "Hur kommer vi igång?", a: "Skicka in formuläret nedan så bokar vi ett möte (videosamtal eller på plats hos er). Vi sätter samman ett förslag baserat på er säsong, era event och er volym. Pris alltid via mejl." },
];

export const Route = createFileRoute("/partner")({
  head: () => ({
    meta: [
      { title: "Partnerprogram för hotell & konferens | Event Sverige" },
      { name: "description", content: "Ramavtal, prioriterad bokning och dedikerad kontakt för hotell, simhallar, äventyrsbad och konferensanläggningar som arrangerar flera event per år." },
      { property: "og:title", content: "Partner med Event Sverige" },
      { property: "og:description", content: "För återkommande kunder. Pris, prioritet och dedikerad partner — utan volymkrav." },
      { property: "og:url", content: "/partner" },
    ],
    links: [{ rel: "canonical", href: "https://eventsverige.se/partner" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(faqJsonLd(FAQ)) },
      { type: "application/ld+json", children: JSON.stringify(serviceJsonLd("Partnerprogram för återkommande kunder", "Ramavtal för hotell, simhall, äventyrsbad och konferens.", "/partner")) },
    ],
  }),
  component: PartnerPage,
});

function PartnerPage() {
  return (
    <div>
      <section className="relative bg-primary text-primary-foreground">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 lg:py-22">
          <span className="text-xs font-bold uppercase tracking-widest text-accent">Partnerprogram</span>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[0.98] max-w-4xl">Bli partner — för er som arrangerar event flera gånger om året</h1>
          <p className="mt-6 text-lg text-primary-foreground/85 max-w-3xl leading-relaxed">Hotell, simhallar, äventyrsbad och konferensanläggningar — ni som har återkommande gäster som efterfrågar aktiviteter, fun food och upplevelser. Vi sätter ihop ett ramavtal som ger er pris, prioritet och en partner som finns hela vägen.</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { Icon: Hotel, title: "Hotell", desc: "Aktiviteter för konferensgäster, kickoffer och bröllop." },
            { Icon: Waves, title: "Simhall & äventyrsbad", desc: "Vattenaktiviteter och uppblåsbara hinderbanor i pool." },
            { Icon: Building2, title: "Konferensanläggning", desc: "Teambuilding-paket som höjer kundens upplevelse." },
            { Icon: Handshake, title: "Återkommande kund", desc: "Många event per år, oavsett bransch." },
          ].map(({ Icon, title, desc }) => (
            <div key={title} className="rounded-2xl bg-card border border-border p-6">
              <Icon className="h-6 w-6 text-accent mb-3" />
              <h3 className="font-display text-lg font-bold mb-2">{title}</h3>
              <p className="text-sm text-muted-foreground">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-secondary/40 border-y border-border">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
          <h2 className="font-display text-2xl sm:text-3xl font-bold mb-8">Vad ni får</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              "Ramavtalspris — bättre än listpris, baserat på er volym.",
              "Prioriterad bokning under högsäsong (jul, lov, sommar).",
              "Dedikerad kontaktperson som lär känna era event och er logistik.",
              "Möjlighet att lagerhålla återkommande material hos oss för snabba ryck.",
              "Gemensamma paket vi kan sälja in till era gäster (teambuilding, familjedag).",
              "Tydlig fakturering och rapportering — passar er ekonomiavdelning.",
            ].map((p) => (
              <div key={p} className="rounded-2xl bg-card border border-border p-5">
                <CheckCircle2 className="h-5 w-5 text-accent mb-3" />
                <p className="text-sm leading-relaxed">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="ansok" className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-14">
        <h2 className="font-display text-2xl sm:text-3xl font-bold mb-2">Ansök om partnerskap</h2>
        <p className="text-muted-foreground mb-8">Fyll i nedan så bokar vi ett möte. Inga bindande åtaganden — vi tar fram ett förslag som matchar er säsong och volym.</p>
        <PartnerForm />
      </section>

      <section className="bg-secondary/40 border-y border-border">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-14">
          <h2 className="font-display text-2xl sm:text-3xl font-bold mb-8">Vanliga frågor</h2>
          <div className="space-y-4">
            {FAQ.map((f, i) => (
              <details key={i} className="group rounded-xl border border-border bg-card p-5">
                <summary className="cursor-pointer font-semibold list-none flex justify-between gap-4">
                  <span>{f.q}</span>
                  <span className="text-accent group-open:rotate-45 transition shrink-0">+</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-14 text-center">
          <h2 className="font-display text-3xl font-extrabold">Frågor först?</h2>
          <p className="mt-3 text-primary-foreground/80">Hör av er via mejl så återkommer vi.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/kontakt" className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3 font-bold hover:bg-accent/90 transition">
              Kontakta oss <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function PartnerForm() {
  const submit = useServerFn(submitPartnerApplication);
  const [form, setForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    partner_type: "hotell" as "hotell" | "simhall" | "aventyrsbad" | "konferens" | "annat",
    events_per_year: "",
    seasons: "",
    message: "",
  });
  const [done, setDone] = useState(false);

  const mutation = useMutation({
    mutationFn: (data: typeof form) => submit({ data }),
    onSuccess: () => {
      setDone(true);
      toast.success("Tack! Vi hör av oss.");
    },
    onError: (e: Error) => toast.error(e.message || "Något gick fel"),
  });

  if (done) {
    return (
      <div className="rounded-2xl border border-border bg-card p-8 text-center">
        <CheckCircle2 className="h-10 w-10 text-accent mx-auto mb-3" />
        <h3 className="font-display text-xl font-bold">Tack — vi hör av oss</h3>
        <p className="mt-2 text-muted-foreground">Vi återkommer via mejl för att boka ett möte.</p>
      </div>
    );
  }

  const input = "w-full rounded-lg border border-border bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent";

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate(form);
      }}
      className="space-y-4 rounded-2xl border border-border bg-card p-6"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="text-sm font-semibold mb-1 block">Företag *</span>
          <input required maxLength={200} className={input} value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
        </label>
        <label className="block">
          <span className="text-sm font-semibold mb-1 block">Typ av verksamhet *</span>
          <select className={input} value={form.partner_type} onChange={(e) => setForm({ ...form, partner_type: e.target.value as typeof form.partner_type })}>
            <option value="hotell">Hotell</option>
            <option value="simhall">Simhall</option>
            <option value="aventyrsbad">Äventyrsbad</option>
            <option value="konferens">Konferensanläggning</option>
            <option value="annat">Annat</option>
          </select>
        </label>
        <label className="block">
          <span className="text-sm font-semibold mb-1 block">Kontaktperson *</span>
          <input required maxLength={200} className={input} value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
        </label>
        <label className="block">
          <span className="text-sm font-semibold mb-1 block">E-post *</span>
          <input required type="email" maxLength={320} className={input} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </label>
        <label className="block">
          <span className="text-sm font-semibold mb-1 block">Telefon</span>
          <input maxLength={50} className={input} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
        </label>
        <label className="block">
          <span className="text-sm font-semibold mb-1 block">Event per år</span>
          <input maxLength={50} placeholder="t.ex. 12" className={input} value={form.events_per_year} onChange={(e) => setForm({ ...form, events_per_year: e.target.value })} />
        </label>
      </div>
      <label className="block">
        <span className="text-sm font-semibold mb-1 block">Säsong / period</span>
        <input maxLength={200} placeholder="t.ex. juni–augusti och v.44–v.50" className={input} value={form.seasons} onChange={(e) => setForm({ ...form, seasons: e.target.value })} />
      </label>
      <label className="block">
        <span className="text-sm font-semibold mb-1 block">Berätta mer (frivilligt)</span>
        <textarea maxLength={5000} rows={4} className={input} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
      </label>
      <button
        type="submit"
        disabled={mutation.isPending}
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-accent text-accent-foreground px-6 py-3 font-bold hover:bg-accent/90 disabled:opacity-50 transition"
      >
        {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Handshake className="h-4 w-4" />}
        Skicka ansökan
      </button>
      <p className="text-xs text-muted-foreground text-center">
        Vi svarar via mejl. Inga bindande åtaganden.
      </p>
    </form>
  );
}
